import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { formatAmountWithSpaces } from '@/lib/amountUtils';
import { externalUrls } from '@/constants/urls';
import { refUrlService } from '@/features/auth/services/refUrlService';
import { routes } from '@/routes/routes';
import { BrokerOfferCard } from '../../components/BrokerOfferCard';
import { useBrokerOffer, useOfferExpiry } from '../../hooks';
import { BrokerProductType } from '../../types/brokerOffer';
import styles from './BrokerOfferViewPage.module.scss';

export function BrokerOfferViewPage() {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const { offer, isLoading, error } = useBrokerOffer(offerId);
  const { isExpired } = useOfferExpiry(offer);

  // Persist the affiliate/CPS landing URL (utm/ref params) on arrival, so the
  // attribution survives the broker offer -> confirm-phone -> otp flow.
  useEffect(() => {
    refUrlService.capture();
  }, []);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !offer) {
    return (
      <div className={styles.root}>
        <div className={styles.errorState}>
          <p className={styles.errorTitle}>Oferta nu este disponibilă</p>
          <p className={styles.errorText}>
            Linkul ofertei este invalid sau a expirat.
          </p>
        </div>
      </div>
    );
  }

  const isCreditLine = offer.productType === BrokerProductType.CreditLine;
  const monthlyPayment = offer.rates?.[0]?.amount;
  const effectiveAnnualRate = offer.effectiveAnnualRate.toLocaleString('ro-RO');
  const nominalAnnualPercentageRate = offer.nominalAnnualPercentageRate.toLocaleString('ro-RO');

  const handleCta = () => {
    if (isExpired) {
      window.location.href = externalUrls.volt.appDownload;
      return;
    }
    navigate(routes.brokerOffer.confirmPhone(offerId as string));
  };

  return (
    <div className={clsx(styles.root, isCreditLine && styles['root--scroll'])}>
      <BrokerOfferCard offer={offer} />

      <div className={styles.details}>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Suma</span>
          <span className={styles.detailValue}>
            {formatAmountWithSpaces(offer.amount)} lei
          </span>
        </div>
        {!isCreditLine && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Rata lunară</span>
            <span className={styles.detailValue}>
              {formatAmountWithSpaces(monthlyPayment)} lei
            </span>
          </div>
        )}
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Durata</span>
          <span className={styles.detailValue}>{offer.numberOfRates} luni</span>
        </div>
      </div>

      {isCreditLine ? (
        <div className={styles.moreInfo}>
          <p className={styles.moreInfoText}>
            Ai mereu la dispoziție o limită de cash și cumpărături. Poți retrage
            toată suma sau o parte din ea. Plătești doar dacă retragi.
          </p>
          <p className={styles.moreInfoText}>
            La scadență, vei plăti costul pentru zilele în care ai folosit banii
            retrași. Dacă nu rambursezi toată suma, poți plăti doar minimul:
            costul utilizării + 5% din suma retrasă. Cei 5% se întorc imediat în
            soldul tău și îi poți folosi din nou.
          </p>
        </div>
      ) : (
        // DAE reuses `interest` as a placeholder until the API exposes the effective annual rate
        <p className={styles.apr}>
          Rata anuală a dobânzii fixe, {nominalAnnualPercentageRate}%, DAE: {effectiveAnnualRate}%.
        </p>
      )}

      <Button
        variant="filled"
        size="medium"
        className={styles.cta}
        text={isExpired ? 'Obține o ofertă nouă în Volt' : 'Continuă'}
        rightIcon={<Icon name="arrow-right" size={16} />}
        onClick={handleCta}
        trackingId={isExpired ? 'broker_offer_get_new' : 'broker_offer_continue'}
      />
    </div>
  );
}
